import {
    Box,
    Grid,
    TextField,
    MenuItem,
    Switch,
    FormControlLabel,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
} from '@mui/material';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import UpdateApplicationServer from "./UpdateApplicationServer";


import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'src/components/snackbar/useSnackbar';
import { authenticationService } from 'src/services';
import { get, post, deleteEntry } from "src/services/httpClient.service";
import LoadingButton from "@mui/lab/LoadingButton";

const ApplicationServer = () => {
    const { showSnackbar } = useSnackbar();
    const API_URL = process.env.REACT_APP_BASE_API_URL;
    const { vendorId } = authenticationService.getVendorFranchiseIds();
    const [rows, setRows] = useState<any[]>([]);
    const [editOpen, setEditOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteId, setDeleteId] = useState<string | null>(null);

    const TYPE_OPTIONS = [
        { label: 'DRM', value: 'DRM' },
        { label: 'CRM', value: 'CRM' },
        { label: 'Others', value: 'OTHER' },
    ];

    const columns: GridColDef[] = [
        { field: 'name', headerName: 'Name', flex: 1 },
        { field: 'url', headerName: 'URL', flex: 1.5 },
        { field: 'serverType', headerName: 'Server Type', flex: 1 },
        { field: 'serverRole', headerName: 'Role', flex: 1 },
        { field: 'healthStatus', headerName: 'Health', flex: 1 },
        { field: 'priority', headerName: 'Priority', flex: 1 },
        {
            field: 'isActive',
            headerName: 'Active',
            flex: 1,
            valueGetter: (params) => (params.row.isActive ? 'Yes' : 'No'),
        }, {
            field: "action",
            headerName: "Action",
            width: 130,
            sortable: false,
            renderCell: (params) => (
                <>
                    <IconButton
                        color="primary"
                        onClick={() => handleEdit(params.row)}
                    >
                        <EditIcon />
                    </IconButton>

                    <IconButton
                        color="error"
                        onClick={() => {
                            setDeleteId(params.row.id);
                            setDeleteDialogOpen(true);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </>
            ),
        }
    ];

    const [open, setOpen] = useState(false);

    const fetchApplicationServers = async () => {
        setLoading(true)
        try {
            const response = await get(
                `${API_URL}/api/vendor-application-servers/vendor/${vendorId}/findAll`
            );

            if (response) {
                setRows(response.data || response || []);
            }
        } catch (error: any) {
            console.error(error);

            showSnackbar(
                error?.response?.data?.message ||
                "Failed to fetch Application Servers",
                "error"
            );
        } finally {
            setLoading(false)
        }
    };

    useEffect(() => {
        if (vendorId) {
            fetchApplicationServers();
        }
    }, [vendorId]);
    useEffect(() => {
        fetchApplicationServers();
    }, []);
    const [formData, setFormData] = useState({
        name: '',
        url: '',
        serverType: '',
        serverRole: 'MAIN',
        healthStatus: 'UP',
        isActive: true,
        priority: 1,
        serverUserName: '',
        serverPassword: '',
        vendorId: vendorId,
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]:
                name === 'priority' || name === 'failureCount'
                    ? Number(value)
                    : value,
        }));
    };

    const handleSave = async () => {
        setSaveLoading(true);

        try {
            const payload = {
                id: "",
                name: formData.name,
                url: formData.url,
                serverRole: formData.serverRole,
                serverType: formData.serverType,
                healthStatus: formData.healthStatus,
                priority: formData.priority,
                serverPassword: formData.serverPassword,
                serverUserName: formData.serverUserName,
                isActive: formData.isActive,
                vendorId
            };

            const response = await post(
                `${API_URL}/api/vendor-application-servers`,
                payload
            );

            if(response && response?.statusCode===200) {
                showSnackbar(
                    response?.message || "Application Server added successfully",
                    "success"
                );

                fetchApplicationServers();

                setOpen(false);

                setFormData({
                    name: "",
                    url: "",
                    serverType: "",
                    serverRole: "MAIN",
                    healthStatus: "UP",
                    isActive: true,
                    priority: 1,
                    serverUserName: "",
                    serverPassword: "",
                    vendorId,
                });
            } else {
                showSnackbar(
                    response?.message || "Error While Creating.",
                    "error"
                );
            }
        } catch (error: any) {
            //console.log('error',error)
            showSnackbar(
                error?.response?.data?.message || "Failed to save Application Server",
                "error"
            );
        } finally {
            setSaveLoading(false);
        }
    };

    const handleEdit = (row: any) => {
        setSelectedRow(row);
        setEditOpen(true);
    };

    const handleDelete = async () => {
        if (!deleteId) return;

        setDeleteLoading(true);

        try {
            const response = await deleteEntry(
                `${API_URL}/api/vendor-application-servers/${deleteId}`
            );

            showSnackbar(
                response?.message || "Application Server deleted successfully",
                "success"
            );

            setDeleteDialogOpen(false);
            setDeleteId(null);

            fetchApplicationServers();
        } catch (error: any) {
            showSnackbar(
                error?.response?.data?.message ||
                error?.message ||
                "Delete Failed",
                "error"
            );
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <Box p={3}>

            <UpdateApplicationServer
                open={editOpen}
                onClose={() => {
                    setEditOpen(false);
                    fetchApplicationServers();
                }}
                data={selectedRow}
            />
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
            >
                <Typography variant="h5">
                    Application Servers
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpen(true)}
                >
                    Add Application Server
                </Button>
            </Box>

            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10]}
                disableSelectionOnClick
                loading={loading}
            />

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Add Application Server</DialogTitle>

                <DialogContent>
                    <Grid container spacing={2} mt={1}>
                        {/* Paste all your existing Grid fields here */}

                        {/* Start */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Server Name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Server URL"
                                name="url"
                                value={formData.url}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="serverType"
                                name="serverType"
                                value={formData.serverType}
                                onChange={handleChange}
                              >
                                {TYPE_OPTIONS.map((type) => (
                                  <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                  </MenuItem>
                                ))}
                            </TextField>
                            {/* <TextField
                                fullWidth
                                label="Server Type"
                                name="serverType"
                                value={formData.serverType}
                                onChange={handleChange}
                            /> */}
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Server Role"
                                name="serverRole"
                                value={formData.serverRole}
                                onChange={handleChange}
                            >
                                <MenuItem value="MAIN">MAIN</MenuItem>
                                <MenuItem value="BACKUP">BACKUP</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Health Status"
                                name="healthStatus"
                                value={formData.healthStatus}
                                onChange={handleChange}
                            >
                                <MenuItem value="UP">UP</MenuItem>
                                <MenuItem value="DOWN">DOWN</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Priority"
                                name="priority"
                                value={formData.priority}
                                onChange={handleChange}
                            />
                        </Grid>

                        {/* <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Failure Count"
                                name="failureCount"
                                value={formData.failureCount}
                                onChange={handleChange}
                            />
                        </Grid> */}

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Server Username"
                                name="serverUserName"
                                value={formData.serverUserName}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                type="password"
                                label="Server Password"
                                name="serverPassword"
                                value={formData.serverPassword}
                                onChange={handleChange}
                            />
                        </Grid>


                        <Grid item xs={12} md={3}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                isActive: e.target.checked,
                                            })
                                        }
                                    />
                                }
                                label="Active"
                            />
                        </Grid>

                        {/* <Grid item xs={12} md={3}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.autoFailover}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                autoFailover: e.target.checked,
                                            })
                                        }
                                    />
                                }
                                label="Auto Failover"
                            />
                        </Grid> */}
                        {/* End */}
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)}>
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saveLoading}
                    >
                        {saveLoading ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete Application Server</DialogTitle>

                <DialogContent>
                    Are you sure you want to delete this Application Server?
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => {
                            setDeleteDialogOpen(false);
                            setDeleteId(null);
                        }}
                        disabled={deleteLoading}
                    >
                        Cancel
                    </Button>

                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleteLoading}
                    >
                        {deleteLoading ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ApplicationServer;